<template>
  <section class="page">
    <div class="page__head">
      <h2 class="page__title">{{ $t('admin.usersHeader') }}</h2>
      <div class="page__search">
        <input class="page__search-input" v-model="q" type="text" :placeholder="$t('admin.searchPh')" />
      </div>
      <ActionButton :color="'#2A8A1B'" :text="$t('admin.addUser')" @click="openCreate" />
    </div>

    <div v-if="error" class="error-banner">{{ error }}</div>

    <div class="page__table">
      <DataTable
        :columns="columns"
        :rows="filteredRows"
        :row-key="(r) => r.id"
        :row-height="56"
        :header-height="48"
      >
        <template #cell-iin="{ row }"><div class="cell-text">{{ row.iin }}</div></template>
        <template #cell-personal_number="{ row }"><div class="cell-text">{{ row.personal_number }}</div></template>
        <template #cell-full_name="{ row }"><div class="cell-text">{{ row.full_name }}</div></template>
        <template #cell-rank_name="{ row }"><div class="cell-text">{{ row.rank_name || '—' }}</div></template>
        <template #cell-position_name="{ row }"><div class="cell-text">{{ row.position_name || '—' }}</div></template>
        <template #cell-flags="{ row }">
          <div class="cell-text">
            <span v-if="row.is_superuser" class="badge badge--super">{{ $t('admin.roleSuper') }}</span>
            <span v-else-if="row.is_staff" class="badge badge--staff">{{ $t('admin.roleSuper') }}</span>
            <span v-else class="badge">{{ $t('admin.roleUser') }}</span>
          </div>
        </template>

        <template #head-actions>
          <div class="tbl__header-text" style="justify-content:flex-end;text-align:right;width:100%;"></div>
        </template>
        <template #cell-actions="{ row }">
          <div class="actions">
            <button class="actions__btn actions__btn--edit" @click="openEdit(row)" :title="$t('nav.edit')">
              <img class="actions__icon" src="../../assets/images/edit.svg" alt="edit" />
            </button>
            <button class="actions__btn actions__btn--delete" @click="onDelete(row)" :title="$t('nav.delete')">
              <img class="actions__icon" src="../../assets/images/markdown/trash.svg" alt="delete" />
            </button>
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Модалка создания/редактирования — полное личное дело -->
    <div v-if="modalOpen" class="modal" @click.self="closeModal">
      <div class="modal__panel modal__panel--wide">
        <h3 class="modal__title">{{ modalTitle }}</h3>

        <!-- Секционные вкладки -->
        <div class="tabs">
          <button
            v-for="s in sections"
            :key="s.key"
            type="button"
            class="tabs__btn"
            :class="{ 'is-active': activeSection === s.key }"
            @click="activeSection = s.key"
          >{{ s.label }}</button>
        </div>

        <div class="form">
          <!-- Учётная запись: только то что нужно для входа в систему -->
          <template v-if="activeSection === 'main'">
            <div class="form__hint" style="margin-bottom: 12px;">
              Данные для входа в систему. Личные данные военнослужащего
              (звание, должность, служба, образование и др.) редактируются в
              разделе «Личный состав» (КПС / ЧПГО).
            </div>
            <div class="form__row">
              <label class="form__label">ИИН (12 цифр) <span class="req">*</span></label>
              <input class="form__input" v-model="form.iin" :disabled="!!editingId" maxlength="12" placeholder="123456789012" />
            </div>
            <div class="form__row">
              <label class="form__label">ФИО <span class="req">*</span></label>
              <input class="form__input" v-model="form.full_name" placeholder="Иванов Иван Иванович" />
            </div>
            <div class="form__row">
              <label class="form__label">Личный номер <span class="req">*</span></label>
              <input class="form__input" type="number" v-model.number="form.personal_number" placeholder="1234" />
            </div>
            <div class="form__row">
              <label class="form__label">
                Пароль {{ editingId ? '(оставьте пустым — не менять)' : '' }}
                <span v-if="!editingId" class="req">*</span>
              </label>
              <input class="form__input" type="password" v-model="form.password" placeholder="минимум 6 символов" />
            </div>
          </template>

          <!-- По фидбеку 02.09.2026: личные данные, служба, назначения,
               квалификация, образование перенесены в /kps/personnel и
               /chpo/personnel — там правится сам военнослужащий. Здесь только
               учётка + права. -->

          <!-- Права доступа -->
          <template v-if="activeSection === 'access'">
            <div class="form__row form__row--checkboxes">
              <label class="checkbox"><input type="checkbox" v-model="form.is_active" /> Активен</label>
              <label class="checkbox"><input type="checkbox" v-model="form.is_system_user" /> Может входить в систему</label>
              <label class="checkbox"><input type="checkbox" v-model="form.is_staff" /> Доступ в Django-админку</label>
              <label class="checkbox"><input type="checkbox" v-model="form.is_superuser" /> Суперадмин (все права)</label>
              <label class="checkbox"><input type="checkbox" v-model="form.is_stpn" /> Может быть СТПН</label>
            </div>

            <!-- Шаблоны ролей — один клик применяет набор permissions -->
            <div v-if="permissionTemplates.length" class="form__section-title">Шаблоны ролей</div>
            <div class="role-templates">
              <button
                v-for="t in permissionTemplates"
                :key="t.code"
                type="button"
                class="role-template-btn"
                :class="{ 'is-active': isTemplateActive(t) }"
                @click="applyTemplate(t)"
              >{{ t.name }}</button>
              <button type="button" class="role-template-btn role-template-btn--clear" @click="form.permissions_v2 = []">
                Снять все
              </button>
            </div>

            <!-- Матрица разрешений по модулям -->
            <div v-if="permissionsByModule.length" class="form__section-title">Разрешения по модулям</div>
            <div v-for="mod in permissionsByModule" :key="mod.module" class="permissions-module">
              <div class="permissions-module__head">
                <b>{{ moduleLabel(mod.module) }}</b>
                <span class="permissions-module__count">{{ mod.items.length }}</span>
                <button type="button" class="permissions-module__all" @click="toggleModuleAll(mod)">
                  {{ moduleAllOn(mod) ? 'Снять все' : 'Отметить все' }}
                </button>
              </div>
              <div class="permissions-module__grid">
                <label v-for="p in mod.items" :key="p.code" class="checkbox permissions-check">
                  <input
                    type="checkbox"
                    :value="p.code"
                    :checked="form.permissions_v2.includes(p.code)"
                    @change="togglePermission(p.code, $event.target.checked)"
                    :disabled="!!form.is_superuser"
                  />
                  <span>{{ p.label }}</span>
                  <code class="perm-code">{{ p.code }}</code>
                </label>
              </div>
            </div>

            <div class="form__hint">
              Суперадмин обходит все проверки — галочки для него неактивны.
              У остальных пользователей доступ определяется отмеченными пунктами.
            </div>
          </template>

          <div v-if="formError" class="form__error">{{ formError }}</div>
        </div>

        <div class="modal__footer">
          <button class="btn-secondary" @click="closeModal">Отмена</button>
          <button class="btn-primary" :disabled="saving" @click="onSubmit">
            {{ saving ? 'Сохранение…' : (editingId ? 'Сохранить' : 'Создать') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import DataTable from '@/components/table/DataTable.vue'
import ActionButton from '@/components/widgets/ActionButton.vue'
import SelectField from '@/components/widgets/SelectField.vue'
import DatePicker from '@/components/widgets/DatePicker.vue'
import { listAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser } from '@/api/adminUsers'
import { getRanks, getPositions } from '@/api/personnel'

const emptyForm = () => ({
  // основное
  iin: '', personal_number: null, full_name: '', password: '',
  rank: null, position: null, user_category: null,
  // личные данные
  birth_date: '', birthplace: '', address: '', phone_number: '',
  id_number: null, family_status: '',
  // служба
  service_start_date: '', recruitment_office: '',
  arrival_date: '', arrival_unit: '',
  departure_date: '', departure_unit: '',
  // назначения
  appointment_date: '', appointment_order_number: null,
  sentry_leader_date: '', sentry_leader_order_number: null,
  // квалификация
  class_qualification: '', class_qualification_date: '', class_qualification_order_number: null,
  sport_rank: null, sport_rank_year: null, sport_type: '',
  combat_training_first_period: null, combat_training_second_period: null,
  // образование
  education_category: '', education_year: null, education_institution: '', education_specialty: '',
  // доступ
  is_active: true, is_staff: false, is_superuser: false, is_stpn: false,
  is_system_user: true,
  permissions_v2: [],
})

export default {
  name: 'AdminUsersView',
  components: { DataTable, ActionButton, SelectField, DatePicker },
  data() {
    return {
      q: '',
      rows: [],
      error: null,
      // По ТЗ 03.09.2026: заголовки локализованы через computed `columns`.
      _colsBase: [
        { key: 'iin',             i18n: 'admin.colIin',         width: 140 },
        { key: 'personal_number', i18n: 'admin.colPersonalNum', width: 110 },
        { key: 'full_name',       i18n: 'admin.colFio',         width: 260, flex: true },
        { key: 'rank_name',       i18n: 'admin.colRank',        width: 160 },
        { key: 'position_name',   i18n: 'admin.colPosition',    width: 220 },
        { key: 'flags',           i18n: 'admin.colRole',        width: 200 },
        { key: 'actions',         i18n: '',                     width: 110 },
      ],
      modalOpen: false,
      editingId: null,
      form: emptyForm(),
      saving: false,
      formError: null,
      rankOptions: [],
      positionOptions: [],
      // Матрица прав (загружается из /api/users/permissions-map/)
      allPermissions: [],
      permissionTemplates: [],
      // Вкладки формы полного личного дела
      activeSection: 'main',
      // По фидбеку 02.09.2026: /admin/users — только «учётка + права».
      // Личные данные / служба / назначения / квалификация / образование —
      // это данные ВОЕННОСЛУЖАЩЕГО, они правятся в /kps/personnel и /chpo/personnel.
      sections: [
        { key: 'main',   label: 'Учётная запись' },
        { key: 'access', label: 'Права доступа' },
      ],
    }
  },
  computed: {
    columns() {
      // По ТЗ 03.09.2026: label колонок берётся из i18n (kz/ru).
      return this._colsBase.map(c => ({
        key: c.key,
        label: c.i18n ? this.$t(c.i18n) : '',
        width: c.width,
        ...(c.flex ? { flex: true } : {}),
      }))
    },
    // Матрица permissions (из /api/users/permissions-map/) сгруппированная по модулям
    permissionsByModule() {
      const groups = {}
      for (const p of this.allPermissions) {
        (groups[p.module] = groups[p.module] || []).push(p)
      }
      return Object.entries(groups).map(([module, items]) => ({ module, items }))
    },
    filteredRows() {
      const qq = this.q.trim().toLowerCase()
      if (!qq) return this.rows
      return this.rows.filter(r =>
        String(r.iin || '').toLowerCase().includes(qq) ||
        String(r.full_name || '').toLowerCase().includes(qq) ||
        String(r.personal_number || '').toLowerCase().includes(qq)
      )
    },
    modalTitle() {
      return this.editingId ? `Редактирование: ${this.form.full_name || this.form.iin}` : 'Новый пользователь'
    },
  },
  mounted() {
    this.fetchAll()
    this.fetchDictionaries()
    this.fetchPermissionsMap()
  },
  methods: {
    async fetchPermissionsMap() {
      try {
        const { http } = await import('@/api/http')
        const { data } = await http.get('users/permissions-map/')
        this.allPermissions = data.permissions || []
        this.permissionTemplates = data.templates || []
      } catch (e) {
        console.warn('Не удалось загрузить permissions-map', e)
      }
    },
    moduleLabel(mod) {
      return {
        chpo: 'ЧПГО (принятие решений)',
        kps: 'КПС (книга погранслужбы)',
        wms: 'Склад / войсковое хозяйство',
        bsfp: 'БСФП',
        vir: 'ВИР',
        mpo: 'МПО (социометрия)',
        admin: 'Администрирование',
      }[mod] || mod
    },
    togglePermission(code, checked) {
      const set = new Set(this.form.permissions_v2 || [])
      if (checked) set.add(code); else set.delete(code)
      this.form.permissions_v2 = [...set]
    },
    toggleModuleAll(mod) {
      const set = new Set(this.form.permissions_v2 || [])
      const allOn = mod.items.every(p => set.has(p.code))
      if (allOn) mod.items.forEach(p => set.delete(p.code))
      else mod.items.forEach(p => set.add(p.code))
      this.form.permissions_v2 = [...set]
    },
    moduleAllOn(mod) {
      const set = new Set(this.form.permissions_v2 || [])
      return mod.items.every(p => set.has(p.code))
    },
    applyTemplate(t) {
      this.form.permissions_v2 = [...(t.permissions || [])]
    },
    isTemplateActive(t) {
      const set = new Set(this.form.permissions_v2 || [])
      if (set.size !== (t.permissions || []).length) return false
      return (t.permissions || []).every(p => set.has(p))
    },
    async fetchAll() {
      try {
        this.error = null
        this.rows = await listAdminUsers()
      } catch (e) {
        this.error = 'Не удалось загрузить пользователей: ' + (e?.payload?.detail || e.message)
      }
    },
    async fetchDictionaries() {
      try {
        const [ranks, positions] = await Promise.all([getRanks(), getPositions()])
        this.rankOptions = (ranks || []).map(r => ({ value: r.id, label: r.name || r.title }))
        this.positionOptions = (positions || []).map(p => ({ value: p.id, label: p.name || p.title }))
      } catch (e) {
        console.warn('Не удалось загрузить справочники', e)
      }
    },
    openCreate() {
      this.editingId = null
      this.form = emptyForm()
      this.formError = null
      this.activeSection = 'main'
      this.modalOpen = true
    },
    openEdit(row) {
      this.editingId = row.id
      // Заполняем из row — все поля личного дела. Незаполненные остаются пустыми.
      this.form = { ...emptyForm() }
      const passThrough = [
        'iin','personal_number','full_name',
        'rank','position','user_category',
        'birth_date','birthplace','address','phone_number','id_number','family_status',
        'service_start_date','recruitment_office',
        'arrival_date','arrival_unit','departure_date','departure_unit',
        'appointment_date','appointment_order_number',
        'sentry_leader_date','sentry_leader_order_number',
        'class_qualification','class_qualification_date','class_qualification_order_number',
        'sport_rank','sport_rank_year','sport_type',
        'combat_training_first_period','combat_training_second_period',
        'education_category','education_year','education_institution','education_specialty',
        'is_active','is_staff','is_superuser','is_stpn',
        'is_system_user','permissions_v2',
      ]
      for (const k of passThrough) {
        if (row[k] !== undefined && row[k] !== null) this.form[k] = row[k]
      }
      // rank/position приходят как id или объекты — нормализуем в id
      if (typeof row.rank === 'object' && row.rank) this.form.rank = row.rank.id
      if (typeof row.position === 'object' && row.position) this.form.position = row.position.id
      this.form.password = ''
      this.formError = null
      this.activeSection = 'main'
      this.modalOpen = true
    },
    closeModal() {
      this.modalOpen = false
    },
    async onSubmit() {
      this.formError = null
      // валидация
      if (!this.editingId) {
        if (!/^\d{12}$/.test(String(this.form.iin || ''))) {
          this.formError = 'ИИН должен быть из 12 цифр'
          return
        }
        if (!this.form.password || this.form.password.length < 6) {
          this.formError = 'Пароль обязателен (минимум 6 символов)'
          return
        }
      } else if (this.form.password && this.form.password.length < 6) {
        this.formError = 'Пароль должен быть минимум 6 символов'
        return
      }
      if (!this.form.full_name || !this.form.full_name.trim()) {
        this.formError = 'Укажите ФИО'
        return
      }
      if (!this.form.personal_number) {
        this.formError = 'Укажите личный номер'
        return
      }

      const payload = { ...this.form }
      // На редактирование не отправляем пустой пароль и ИИН
      if (this.editingId) {
        delete payload.iin
        if (!payload.password) delete payload.password
      }
      // Все пустые поля (даты, строки, id) — не отправляем чтобы бэк не жаловался
      // на пустые ISO-даты и не затирал существующие данные пустотой.
      const dateFields = new Set([
        'birth_date','service_start_date','appointment_date','sentry_leader_date',
        'class_qualification_date','arrival_date','departure_date',
      ])
      Object.keys(payload).forEach(k => {
        const v = payload[k]
        // FK — пустые null не шлём
        if (['rank','position','user_category'].includes(k) && !v) {
          delete payload[k]
          return
        }
        // Даты — пустые строки не отправляем
        if (dateFields.has(k) && (!v || v === '')) {
          delete payload[k]
          return
        }
        // number-поля — пустые значения (null) чистим
        if (v === '' || v === null || v === undefined) {
          if (typeof this.form[k] === 'number' || k.endsWith('_year') || k.endsWith('_number') || k === 'id_number') {
            delete payload[k]
          }
        }
      })

      this.saving = true
      try {
        if (this.editingId) {
          await updateAdminUser(this.editingId, payload)
        } else {
          await createAdminUser(payload)
        }
        this.modalOpen = false
        await this.fetchAll()
      } catch (e) {
        const p = e?.payload
        if (p && typeof p === 'object') {
          const parts = []
          for (const [k, v] of Object.entries(p)) {
            parts.push(`${k}: ${Array.isArray(v) ? v.join(' ') : v}`)
          }
          this.formError = parts.join('; ')
        } else {
          this.formError = e.message || 'Ошибка сохранения'
        }
      } finally {
        this.saving = false
      }
    },
    async onDelete(row) {
      const ok = window.confirm(`Удалить пользователя «${row.full_name}»?`)
      if (!ok) return
      try {
        await deleteAdminUser(row.id)
        await this.fetchAll()
      } catch (e) {
        this.error = 'Не удалось удалить: ' + (e?.payload?.detail || e.message)
      }
    },
  },
}
</script>

<style scoped>
.page { background: #fff; border-radius: 16px; padding: 24px; display: flex; flex-direction: column; gap: 16px; }
.page__head { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.page__title { margin: 0; font-size: 24px; font-weight: 700; color: #111827; flex: 1 1 auto; }
.page__search { width: 320px; max-width: 100%; padding: 8px 14px; border-radius: 10px; outline: 1px #E9EAEC solid; outline-offset: -1px; background: #fff; }
.page__search-input { width: 100%; border: none; outline: none; background: transparent; color: #111827; font-size: 14px; }

.error-banner { padding: 10px 14px; border: 1px solid #FCA5A5; background: #FEF2F2; color: #991B1B; border-radius: 8px; }

.cell-text { color: #111827; font-size: 14px; line-height: 20px; }
.badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 12px; font-weight: 600; background: #F3F4F6; color: #374151; margin-right: 6px; }
.badge--super { background: #FCE7F3; color: #9D174D; }
.badge--staff { background: #DBEAFE; color: #1D4ED8; }
.badge--inactive { background: #FEE2E2; color: #991B1B; }

.actions { display: inline-flex; align-items: center; gap: 8px; justify-content: flex-end; width: 100%; }
.actions__btn { width: 30px; height: 30px; padding: 6px; border-radius: 8px; border: 0; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.actions__icon { width: 16px; height: 16px; display: block; }
.actions__btn--edit { background: #2F78EE; }
.actions__btn--delete { background: #D81717; }

.modal { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal__panel { background: #fff; width: 560px; max-width: calc(100% - 32px); max-height: 90vh; overflow-y: auto; border-radius: 12px; padding: 24px; box-shadow: 0 12px 32px rgba(0,0,0,0.2); }
.modal__panel--wide { width: 720px; }

.tabs { display: flex; gap: 4px; margin: 0 -4px 16px; padding-bottom: 8px; border-bottom: 2px solid #E5E7EB; overflow-x: auto; }
.tabs__btn {
  padding: 8px 14px;
  border: 0;
  background: transparent;
  color: #6B7280;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  font-family: inherit;
}
.tabs__btn:hover { color: #374151; background: #F9FAFB; }
.tabs__btn.is-active { color: #2F78EE; border-bottom-color: #2F78EE; }

.form__row--split { flex-direction: row; gap: 12px; }
.form__row--split > div { flex: 1; display: flex; flex-direction: column; gap: 6px; }

.form__section-title {
  margin: 8px 0 4px;
  padding: 6px 10px;
  background: #F3F4F6;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-left: 3px solid #2F78EE;
  border-radius: 3px;
}
.form__hint {
  margin-top: 4px;
  padding: 10px 14px;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  color: #1E3A8A;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
}
.modal__title { margin: 0 0 16px; font-size: 20px; font-weight: 700; color: #111827; }
.modal__footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; }

.form { display: flex; flex-direction: column; gap: 12px; }
.form__row { display: flex; flex-direction: column; gap: 6px; }
.form__row--checkboxes { flex-direction: row; flex-wrap: wrap; gap: 16px; align-items: center; padding-top: 8px; }
.form__label { font-size: 13px; font-weight: 600; color: #374151; }
.req { color: #DC2626; }
.form__input { height: 36px; padding: 6px 12px; border: 1px solid #D1D5DB; border-radius: 8px; font-size: 14px; outline: none; }
.form__input:focus { border-color: #2F78EE; box-shadow: 0 0 0 2px rgba(47,120,238,0.25); }
.form__input:disabled { background: #F3F4F6; color: #6B7280; cursor: not-allowed; }
.form__error { padding: 8px 12px; background: #FEF2F2; border: 1px solid #FCA5A5; color: #991B1B; border-radius: 6px; font-size: 13px; }

.checkbox { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; font-size: 14px; color: #374151; }

.btn-primary, .btn-secondary { padding: 8px 16px; border-radius: 8px; border: 0; cursor: pointer; font-size: 14px; font-weight: 600; }
.btn-primary { background: #2F78EE; color: #fff; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #F3F4F6; color: #374151; }
.btn-secondary:hover { background: #E5E7EB; }
.btn-primary:hover:not(:disabled) { background: #1E5BB8; }

/* Матрица прав (по ТЗ 02.09.2026) */
.role-templates {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 16px;
}
.role-template-btn {
  padding: 8px 14px;
  border: 1px solid #E5E7EB;
  border-radius: 999px;
  background: #F9FAFB;
  color: #374151;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: border-color .15s, background .15s, color .15s;
}
.role-template-btn:hover { border-color: #2F78EE; color: #2F78EE; }
.role-template-btn.is-active { background: #EFF6FF; border-color: #2F78EE; color: #2F78EE; }
.role-template-btn--clear { background: #FEF2F2; color: #B91C1C; border-color: #FCA5A5; }

.permissions-module {
  margin: 8px 0 14px;
  padding: 10px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  background: #FDFDFD;
}
.permissions-module__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.permissions-module__count {
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #EFF6FF;
  color: #2F78EE;
  font-weight: 700;
}
.permissions-module__all {
  margin-left: auto;
  padding: 4px 10px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: #FFFFFF;
  cursor: pointer;
  font-size: 12px;
  color: #6B7280;
}
.permissions-module__all:hover { border-color: #2F78EE; color: #2F78EE; }
.permissions-module__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 6px;
}
.permissions-check {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 6px;
  align-items: center;
  padding: 4px 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
}
.permissions-check:hover { background: #F3F4F6; }
.perm-code {
  font-family: 'Menlo','Courier New',monospace;
  font-size: 10px;
  color: #9CA3AF;
  background: #F9FAFB;
  padding: 1px 4px;
  border-radius: 4px;
}
</style>
